import React from 'react';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { money } from '@/lib/helpers';

export default function DailyClosing() {
  const { data } = useQuery({ queryKey: ['closing'], queryFn: async () => (await api.get('/reports/daily-closing')).data.data });
  const { data: breakdown } = useQuery({ queryKey: ['pay-breakdown'], queryFn: async () => {
    const today = new Date().toISOString().slice(0, 10);
    return (await api.get('/reports/payment-breakdown', { params: { date: today } })).data.data;
  } });
  const methods = data?.by_method || {};
  return (
    <Page>
      <TopBar title="Daily Closing" />
      <div className="px-4 pt-4 space-y-4">
        <div className="rounded-2xl border border-border bg-primary p-5 text-primary-foreground">
          <p className="text-xs opacity-80">Today&apos;s total sales</p>
          <p className="font-mono text-3xl font-bold">{money(data?.total_sales)}</p>
          <p className="mt-1 text-xs opacity-80">{data?.bills || 0} bills · {data?.date}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="mb-3 text-sm font-semibold">Collections by method</p>
          <div className="space-y-2">
            {['CASH', 'UPI', 'CARD', 'BANK', 'CREDIT'].map((m) => (
              <div key={m} className="flex justify-between text-sm" data-testid={`closing-${m}`}><span className="text-muted-foreground">{m}</span><span className="font-mono font-medium">{money(methods[m] || 0)}</span></div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="mb-3 text-sm font-semibold">Collections by account</p>
          {(breakdown?.by_account || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No account-tagged collections today. Pick a UPI bank / card machine at checkout to see the split here.</p>
          ) : (
            <div className="space-y-2">
              {breakdown.by_account.map((a, i) => (
                <div key={i} className="flex justify-between text-sm" data-testid={`closing-account-${i}`}>
                  <span className="text-muted-foreground">{a.method} · <b className="text-foreground">{a.account}</b></span>
                  <span className="font-mono font-medium">{money(a.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-border bg-card p-4"><p className="text-xs text-muted-foreground">Discounts given</p><p className="font-mono text-xl font-semibold">{money(data?.total_discount)}</p></div>
          <div className="rounded-2xl border border-border bg-card p-4"><p className="text-xs text-muted-foreground">Returns</p><p className="font-mono text-xl font-semibold">{data?.returns_count || 0}</p><p className="text-xs text-muted-foreground">{money(data?.returns_value)}</p></div>
          <div className="rounded-2xl border border-border bg-card p-4"><p className="text-xs text-muted-foreground">Expenses</p><p className="font-mono text-xl font-semibold">{money(data?.expenses_total)}</p></div>
          <div className="rounded-2xl border border-border bg-card p-4"><p className="text-xs text-muted-foreground">Net (sales - expenses)</p><p className="font-mono text-xl font-semibold">{money((data?.total_sales || 0) - (data?.expenses_total || 0))}</p></div>
        </div>
      </div>
    </Page>
  );
}
